import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

export const loginInitialState: LoginState = {
  isLoginShown: false,
  isKeepMeLoggedIn: false,
  isBiometricsEnabledOnTempPasswordChange: false,
  trustedDevices: {},
  trustedDeviceLastPrompted: new Date('2000-01-01').toISOString(),
}

const loginSlice = createSlice({
  name: 'login',
  initialState: loginInitialState,
  reducers: {
    setIsLoginShown(
      state,
      {
        payload: {
          shouldShow,
          onLoginSuccessCallback,
          onLoginSuccessRedirectUrl,
          onLoginCancelledRedirectUrl,
        },
      }: PayloadAction<SetIsLoginShown>
    ) {
      state.isLoginShown = shouldShow
      state.onLoginSuccessCallback =
        typeof onLoginSuccessCallback === 'function' ? onLoginSuccessCallback : undefined
      state.onLoginSuccessRedirectUrl = onLoginSuccessRedirectUrl
      state.onLoginCancelledRedirectUrl = onLoginCancelledRedirectUrl
    },

    setIsKeepMeLoggedIn(state, { payload }: PayloadAction<boolean>) {
      state.isKeepMeLoggedIn = payload
    },

    setIsBiometricsEnabledOnTempPasswordChange(state, { payload }: PayloadAction<boolean>) {
      state.isBiometricsEnabledOnTempPasswordChange = payload
    },

    setTrustedDeviceDetails(
      state,
      {
        payload: { accountIdentifier, deviceDetails },
      }: PayloadAction<{ accountIdentifier: string; deviceDetails: TrustedDeviceDetails }>
    ) {
      state.trustedDevices[accountIdentifier] = deviceDetails
    },

    untrustDevice(
      state,
      { payload: { accountIdentifier } }: PayloadAction<{ accountIdentifier: string }>
    ) {
      const trustedDevice = state.trustedDevices[accountIdentifier]
      if (trustedDevice) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.trustedDevices[accountIdentifier]
      }
    },

    rememberTrustedDevice(
      state,
      { payload: { accountIdentifier } }: PayloadAction<{ accountIdentifier: string }>
    ) {
      const trustedDevice = state.trustedDevices[accountIdentifier]
      if (trustedDevice) {
        trustedDevice.isRemembered = true
      }
    },

    recordLastSuccessfulLogin(state, { payload }: PayloadAction<LastSuccessfulLogin>) {
      state.lastSuccessfulLogin = payload
    },

    resetTrustedDevicePrompt(state) {
      state.trustedDeviceLastPrompted = loginInitialState.trustedDeviceLastPrompted
    },

    recordTrustedDevicePrompt(state) {
      state.trustedDeviceLastPrompted = new Date().toISOString()
    },
  },
})

export const {
  setIsLoginShown,
  setIsKeepMeLoggedIn,
  setIsBiometricsEnabledOnTempPasswordChange,
  setTrustedDeviceDetails,
  untrustDevice,
  rememberTrustedDevice,
  recordLastSuccessfulLogin,
  recordTrustedDevicePrompt,
  resetTrustedDevicePrompt,
} = loginSlice.actions

export default persistReducer(
  {
    key: 'login',
    whitelist: ['isKeepMeLoggedIn', 'trustedDevices', 'trustedDeviceLastPrompted'],
    storage,
    debug: !PRODUCTION,
  },
  loginSlice.reducer
)

// =====
// Types
// =====

export interface LoginState {
  isLoginShown: boolean
  /** Server sets extended expiry (shown in app only) */
  isKeepMeLoggedIn: boolean
  trustedDevices: Record<string, TrustedDeviceDetails>
  lastSuccessfulLogin?: LastSuccessfulLogin
  onLoginSuccessRedirectUrl?: string
  onLoginCancelledRedirectUrl?: string
  onLoginSuccessCallback?(): void
  isBiometricsEnabledOnTempPasswordChange: boolean
  trustedDeviceLastPrompted: string
}

export type TrustedDeviceDetails = {
  deviceGroupKey: string
  deviceKey: string
  deviceSecret: string
  isRemembered: boolean
}

export type LastSuccessfulLogin = {
  accountIdentifier: string
  loginDateTime: string
  withMfa: boolean
  withBiometrics: boolean
}

type SetIsLoginShownOpen = Pick<
  LoginState,
  'onLoginSuccessCallback' | 'onLoginSuccessRedirectUrl' | 'onLoginCancelledRedirectUrl'
>
type SetIsLoginShown =
  | ({
      shouldShow: false
    } & { [P in keyof SetIsLoginShownOpen]: never })
  | ({
      shouldShow: true
    } & SetIsLoginShownOpen)
